
const GridSwitcher = {
  STORAGE_KEYS: {
    desktop: 'collectionView',
    mobile: 'collectionMobileView',
  },
  DEFAULT_VIEWS: {
    desktop: 'grid-3',
    mobile: 'grid-mobile-2',
  },
  VIEWS: {
    desktop: ['grid-2', 'grid-3', 'grid-4'],
    mobile: ['grid-mobile-1', 'grid-mobile-2'],
  },
  DESKTOP_GRID_CLASSES: [
    'grid--1-col-desktop',
    'grid--2-col-desktop',
    'grid--3-col-desktop',
    'grid--4-col-desktop',
    'grid--5-col-desktop',
    'grid--6-col-desktop',
  ],
 
  /** Return the configured default for this breakpoint. */
  getDefault(mode) {
    const configuredView = document.querySelector(`[data-grid-switcher="${mode}"]`)?.dataset.defaultView;
    return this.VIEWS[mode].includes(configuredView) ? configuredView : this.DEFAULT_VIEWS[mode];
  },

  /** Return a valid saved view or the configured default. */
  getSaved(mode) {
    try {
      const saved = localStorage.getItem(this.STORAGE_KEYS[mode]);
      return this.VIEWS[mode].includes(saved) ? saved : this.getDefault(mode);
    } catch (error) {
      return this.getDefault(mode);
    }
  },
 
  /** Apply a view class without changing the choice for the other breakpoint. */
  applyView(mode, view) {
    const grid = document.getElementById('product-grid');
    if (!grid || !this.VIEWS[mode].includes(view)) return;

    grid.classList.remove(...this.VIEWS[mode]);
    grid.classList.add(view);

    if (mode === 'desktop') {
      const columns = view.replace('grid-', '');
      grid.classList.remove(...this.DESKTOP_GRID_CLASSES);
      grid.classList.add(`grid--${columns}-col-desktop`);
    }

    this.updateImageSizes(grid);
  },

  /** Keep responsive image candidates in step with both selected layouts. */
  updateImageSizes(grid) {
    if (grid.closest('.collection-catalog')) {
      grid.querySelectorAll('img[sizes]').forEach((image) => {
        const width = Math.ceil(image.clientWidth);
        if (width > 0) image.sizes = `${width}px`;
      });
      if ('ResizeObserver' in window && this.observedGrid !== grid) {
        this.resizeObserver?.disconnect();
        this.observedGrid = grid;
        this.resizeObserver = new ResizeObserver(() => this.updateImageSizes(grid));
        this.resizeObserver.observe(grid);
      }
      return;
    }

    const desktopView = this.VIEWS.desktop.find((view) => grid.classList.contains(view)) || this.getDefault('desktop');
    const mobileView = this.VIEWS.mobile.find((view) => grid.classList.contains(view)) || this.getDefault('mobile');
    const desktopColumns = desktopView.replace('grid-', '');
    const mobileColumns = mobileView.replace('grid-mobile-', '');
    const mobileGutter = mobileColumns === '1' ? '3rem' : '3.5rem';

    grid.querySelectorAll('img[sizes]').forEach((image) => {
      image.sizes = `(min-width: 990px) calc((100vw - 36rem) / ${desktopColumns}), (min-width: 750px) calc((100vw - 12rem) / 3), calc((100vw - ${mobileGutter}) / ${mobileColumns})`;
    });
  },
 
  /** Mark the matching toggle button in one switcher as active. */
  setActive(mode, view) {
    document.querySelectorAll(`[data-grid-switcher="${mode}"] .view-btn`).forEach((btn) => {
      const isActive = btn.dataset.view === view;
      btn.classList.toggle('active', isActive);
      btn.setAttribute('aria-pressed', String(isActive));
    });
  },
 
  /**
   * Called after every facet render to restore the chosen grid layout.
   * (renderProductGridContainer replaces innerHTML, wiping any classes
   *  that were on the grid element, so we must re-apply here.)
   */
  restore() {
    Object.keys(this.VIEWS).forEach((mode) => {
      if (!document.querySelector(`[data-grid-switcher="${mode}"]`)) return;
      const saved = this.getSaved(mode);
      this.applyView(mode, saved);
      this.setActive(mode, saved);
    });
  },
 
  /** Wire up the toggle buttons — safe to call multiple times */
  init() {
    const switchers = document.querySelectorAll('[data-grid-switcher]');
    if (!switchers.length) return;

    this.restore();

    switchers.forEach((switcher) => {
      const mode = switcher.dataset.gridSwitcher;

      switcher.querySelectorAll('.view-btn').forEach((btn) => {
        // Avoid adding duplicate listeners on re-init.
        if (btn.dataset.gridListenerAttached) return;
        btn.dataset.gridListenerAttached = 'true';

        btn.addEventListener('click', (event) => {
          event.preventDefault();
          const view = btn.dataset.view;
          this.applyView(mode, view);
          this.setActive(mode, view);
          try {
            localStorage.setItem(this.STORAGE_KEYS[mode], view);
          } catch (error) {
            // The layout still works when browser storage is unavailable.
          }
        });
      });
    });
  },
};

const CollectionInfiniteScroll = {
  observer: null,
  loading: false,
  generation: 0,
  minimumLoaderTime: 1000,

  init() {
    this.destroy();
    this.generation += 1;
    this.container = document.getElementById('ProductGridContainer');
    this.sentinel = this.container?.querySelector('[data-infinite-scroll-sentinel]');

    if (!this.container || !this.sentinel || !this.getNextUrl() || !('IntersectionObserver' in window)) return;

    this.container.classList.add('infinite-scroll-enabled');
    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.loadNextPage();
      },
      { rootMargin: '0px 0px 300px 0px' }
    );
    this.observer.observe(this.sentinel);
  },

  destroy() {
    if (this.observer) this.observer.disconnect();
    this.container?.classList.remove('infinite-scroll-enabled');
    this.observer = null;
    this.loading = false;
  },

  getNextUrl() {
    return this.container?.querySelector('.pagination__item--prev')?.href || null;
  },

  async loadNextPage() {
    const nextUrl = this.getNextUrl();
    const grid = this.container?.querySelector('#product-grid');
    if (this.loading || !nextUrl || !grid) return;

    const requestGeneration = this.generation;
    const requestUrl = new URL(nextUrl, window.location.origin);
    requestUrl.searchParams.set('section_id', grid.dataset.id);

    this.loading = true;
    this.sentinel.classList.remove('is-error', 'is-complete');
    this.sentinel.classList.add('is-loading');
    this.sentinel.querySelector('.infinite-scroll-loader__text').textContent = 'Loading more products...';

    try {
      const [response] = await Promise.all([
        fetch(requestUrl.toString(), { headers: { Accept: 'text/html' } }),
        new Promise((resolve) => window.setTimeout(resolve, this.minimumLoaderTime)),
      ]);

      if (!response.ok) throw new Error(`Unable to load collection page (${response.status})`);

      const parsedHTML = new DOMParser().parseFromString(await response.text(), 'text/html');
      const incomingContainer = parsedHTML.getElementById('ProductGridContainer');
      const incomingGrid = incomingContainer?.querySelector('#product-grid');

      if (requestGeneration !== this.generation) return;
      if (!incomingGrid) throw new Error('The next collection page did not contain a product grid.');

      const newItems = Array.from(incomingGrid.children);
      grid.append(...newItems);
      GridSwitcher.restore();

      const currentPagination = this.container.querySelector('.pagination-wrapper');
      const incomingPagination = incomingContainer.querySelector('.pagination-wrapper');
      if (incomingPagination) {
        currentPagination?.replaceWith(incomingPagination);
      } else {
        currentPagination?.remove();
      }

      if (typeof initializeScrollAnimationTrigger === 'function') {
        initializeScrollAnimationTrigger(grid);
      }

      if (!this.getNextUrl()) {
        this.observer?.disconnect();
        this.sentinel.classList.add('is-complete');
        this.sentinel.querySelector('.infinite-scroll-loader__text').textContent = 'All products loaded';
      }
    } catch (error) {
      if (requestGeneration !== this.generation) return;
      this.sentinel.classList.add('is-error');
      this.sentinel.querySelector('.infinite-scroll-loader__text').textContent =
        'Products could not be loaded. Scroll again to retry.';
    } finally {
      if (requestGeneration === this.generation) {
        this.loading = false;
        this.sentinel.classList.remove('is-loading');
      }
    }
  },
};


class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    CollectionInfiniteScroll.destroy();
    CollectionInfiniteScroll.generation += 1;
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') {
          initializeScrollAnimationTrigger(document.getElementById('ProductGridContainer'));
        }
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') {
      initializeScrollAnimationTrigger(document.getElementById('ProductGridContainer'));
    }
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });

      // ✅ Re-apply grid view after container is replaced
    GridSwitcher.restore();
    CollectionInfiniteScroll.init();
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

// ✅ Init grid switcher on page load
document.addEventListener('DOMContentLoaded', () => {
  GridSwitcher.init();
  CollectionInfiniteScroll.init();
});
 
// ✅ Re-init after Shopify theme editor section reloads
document.addEventListener('shopify:section:load', () => {
  GridSwitcher.init();
  CollectionInfiniteScroll.init();
});

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.numberInputs = Array.from(this.querySelectorAll('input[name^="filter.v.price"]'));
    this.minInput = this.numberInputs[0];
    this.maxInput = this.numberInputs[1];
    this.minRangeSlider = this.querySelector('.price-slider__input--min');
    this.maxRangeSlider = this.querySelector('.price-slider__input--max');
    this.progressBar = this.querySelector('.price-slider__progress');

    this.onNumberChange = this.onNumberChange.bind(this);
    this.onRangeSliderInput = this.onRangeSliderInput.bind(this);

    this.numberInputs.forEach((element) => {
      element.addEventListener('change', this.onNumberChange);
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });

    if (!this.minRangeSlider || !this.maxRangeSlider || !this.progressBar) return;

    this.minRangeSlider.addEventListener('input', this.onRangeSliderInput);
    this.maxRangeSlider.addEventListener('input', this.onRangeSliderInput);
    this.syncRangeSlidersWithInputs();
  }

  onKeyDown(event) {
    if (event.ctrlKey || event.metaKey) return;

    const allowedKeys = /[0-9]|\.|,|Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(allowedKeys)) event.preventDefault();
  }

  parsePrice(value, fallback = 0) {
    const parsedValue = Number(String(value).replace(/[^0-9.-]/g, ''));
    return Number.isFinite(parsedValue) ? parsedValue : fallback;
  }

  formatPrice(value) {
    return Number(value).toFixed(2);
  }

  onNumberChange(event) {
    const rangeMinimum = Number(this.minRangeSlider?.min || 0);
    const rangeMaximum = Number(this.maxRangeSlider?.max || 0);
    let minValue = this.minInput.value === '' ? rangeMinimum : this.parsePrice(this.minInput.value, rangeMinimum);
    let maxValue = this.maxInput.value === '' ? rangeMaximum : this.parsePrice(this.maxInput.value, rangeMaximum);

    minValue = Math.min(Math.max(minValue, rangeMinimum), rangeMaximum);
    maxValue = Math.min(Math.max(maxValue, rangeMinimum), rangeMaximum);

    if (minValue > maxValue) {
      if (event.currentTarget === this.minInput) minValue = maxValue;
      if (event.currentTarget === this.maxInput) maxValue = minValue;
    }

    if (this.minInput.value !== '') this.minInput.value = this.formatPrice(minValue);
    if (this.maxInput.value !== '') this.maxInput.value = this.formatPrice(maxValue);

    this.syncRangeSlidersWithInputs();
  }

  onRangeSliderInput(event) {
    let minValue = Number(this.minRangeSlider.value);
    let maxValue = Number(this.maxRangeSlider.value);

    if (event.target === this.minRangeSlider && minValue > maxValue) {
      minValue = maxValue;
      this.minRangeSlider.value = String(minValue);
    } else if (event.target === this.maxRangeSlider && maxValue < minValue) {
      maxValue = minValue;
      this.maxRangeSlider.value = String(maxValue);
    }

    this.updateSliderProgress();
    this.syncInputsWithRangeSliders();
  }

  updateSliderProgress() {
    if (!this.progressBar || !this.minRangeSlider || !this.maxRangeSlider) return;

    const minValue = Number(this.minRangeSlider.value);
    const maxValue = Number(this.maxRangeSlider.value);
    const minRange = Number(this.minRangeSlider.min);
    const maxRange = Number(this.minRangeSlider.max);
    const range = maxRange - minRange || 1;
    const leftPercent = ((minValue - minRange) / range) * 100;
    const rightPercent = ((maxRange - maxValue) / range) * 100;

    this.progressBar.style.left = `${leftPercent}%`;
    this.progressBar.style.right = `${rightPercent}%`;
  }

  syncRangeSlidersWithInputs() {
    if (!this.minRangeSlider || !this.maxRangeSlider) return;

    const rangeMinimum = Number(this.minRangeSlider.min);
    const rangeMaximum = Number(this.maxRangeSlider.max);
    const minValue = this.minInput?.value === '' ? rangeMinimum : this.parsePrice(this.minInput.value, rangeMinimum);
    const maxValue = this.maxInput?.value === '' ? rangeMaximum : this.parsePrice(this.maxInput.value, rangeMaximum);

    this.minRangeSlider.value = String(Math.min(Math.max(minValue, rangeMinimum), maxValue));
    this.maxRangeSlider.value = String(Math.max(Math.min(maxValue, rangeMaximum), minValue));
    this.updateSliderProgress();
  }

  syncInputsWithRangeSliders() {
    const minimum = Number(this.minRangeSlider.min);
    const maximum = Number(this.maxRangeSlider.max);
    const minValue = Number(this.minRangeSlider.value);
    const maxValue = Number(this.maxRangeSlider.value);

    if (this.minInput) this.minInput.value = minValue <= minimum ? '' : this.formatPrice(minValue);
    if (this.maxInput) this.maxInput.value = maxValue >= maximum ? '' : this.formatPrice(maxValue);
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);


document.addEventListener("DOMContentLoaded", function () {

  if ("scrollRestoration" in history) {
    history.scrollRestoration = "manual";
  }

  window.scrollTo(0, 0);

  let isFetching = false;

  function loadMore() {
    const btn = document.querySelector(".load-more");
    if (!btn || isFetching) return;

    const url = btn.getAttribute("href");
    if (!url) return;

    isFetching = true;
    btn.innerText = "Loading...";

    fetch(url)
      .then(res => res.text())
      .then(html => {
        const doc = new DOMParser().parseFromString(html, "text/html");

        let newItems = doc.querySelectorAll(
          "#ProductGridContainer .product-grid-wpr > *"
        );
        let container = document.querySelector(
          "#ProductGridContainer .product-grid-wpr"
        );

        if (!newItems.length) {
          newItems = doc.querySelectorAll("#product-grid > *");
          container = document.querySelector("#product-grid");
        }

        if (!container) return;

        newItems.forEach(el => container.appendChild(el));

        const newBtnWrapper = doc.querySelector(".lodeMore-btn-wpr");
        const oldBtnWrapper = document.querySelector(".lodeMore-btn-wpr");

        if (newBtnWrapper) {
          oldBtnWrapper.replaceWith(newBtnWrapper);
        } else if (oldBtnWrapper) {
          oldBtnWrapper.remove();
        }
      })
      .catch(() => {
        const btn = document.querySelector(".load-more");
        if (btn) btn.innerText = "LOAD MORE";
      })
      .finally(() => {
        isFetching = false;
      });
  }

  window.addEventListener(
    "scroll",
    function () {
      const btn = document.querySelector(".lodeMore-btn-wpr");
      if (!btn) return;

      const btnTop = btn.getBoundingClientRect().top;
      const windowHeight = window.innerHeight;

      if (btnTop <= windowHeight + 1000) {
        loadMore();
      }
    },
    { passive: true }
  );
});
